import dayjs from "dayjs";
import PropTypes from "prop-types";

import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

import { formatDate, formatMoney, formatNumber } from "@/features/shared";

// 9.5" x 11" continuous form. The manufacturer's 9.5" "printable" width
// excludes tractor-hole strips, but real printers reserve ~0.5" of
// unprintable margin on each side for the print head's travel range. Padding
// here keeps content inside that practical print area (~8.5" wide).
const PAGE_SIZE = [684, 792];

const styles = StyleSheet.create({
  page: {
    paddingHorizontal: 36,
    paddingVertical: 26,
    fontSize: 11,
    fontFamily: "Helvetica",
    color: "#000",
    lineHeight: 1.2,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  logo: {
    width: 55,
    height: "auto",
  },
  titleColumn: {
    alignItems: "flex-end",
    flexShrink: 1,
  },
  title: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
  },
  titleRef: {
    fontSize: 13,
    marginTop: 12,
  },
  infoCard: {
    borderWidth: 0.5,
    borderColor: "#999",
  },
  infoBand: {
    padding: 8,
    borderTopWidth: 0.5,
    borderColor: "#999",
  },
  infoBandRow: {
    flexDirection: "row",
  },
  infoBandRowDivided: {
    flexDirection: "row",
    borderTopWidth: 0.5,
    borderColor: "#999",
  },
  infoBandCell: {
    flex: 1,
    padding: 8,
  },
  infoBandCellDivided: {
    flex: 1,
    padding: 8,
    borderRightWidth: 0.5,
    borderColor: "#999",
  },
  partyLabel: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    marginBottom: 4,
  },
  partyName: {
    fontFamily: "Helvetica-Bold",
  },
  partyNpwp: {
    marginTop: 2,
  },
  metaLabel: {
    fontFamily: "Helvetica-Bold",
  },
  tableContainer: {
    marginTop: 8,
    borderWidth: 0.5,
    borderColor: "#999",
  },
  tableHeader: {
    flexDirection: "row",
    fontFamily: "Helvetica-Bold",
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderColor: "#999",
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  cell: {
    paddingHorizontal: 4,
    borderRightWidth: 0.5,
    borderColor: "#999",
  },
  rightCell: {
    textAlign: "right",
    paddingHorizontal: 4,
    borderRightWidth: 0.5,
    borderColor: "#999",
  },
  summarySection: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 16,
  },
  summaryColumn: {
    width: "45%",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  summaryLabel: {
    fontFamily: "Helvetica-Bold",
  },
  grandTotalRow: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    marginTop: 4,
    paddingTop: 4,
    borderTopWidth: 0.5,
    borderColor: "#999",
  },
  signatureSection: {
    flexDirection: "row",
    marginTop: 36,
  },
  signatureCell: {
    flex: 1,
    alignItems: "center",
  },
  signatureLabel: {
    fontFamily: "Helvetica-Bold",
  },
  signatureSubColumn: {
    width: "70%",
    marginTop: 56,
  },
  signatureLine: {
    borderBottomWidth: 0.5,
    borderColor: "#000",
  },
  signatureDate: {
    fontSize: 9,
    marginTop: 4,
  },
});

const COL = {
  index: 0.5,
  name: 5,
  qty: 1.2,
  price: 1.5,
  bruto: 1.5,
  disc: 1.5,
  total: 1.5,
};

export const SalesInvoicePdf = ({ salesInvoice }) => {
  const productVariants = [...salesInvoice.salesInvoiceProductVariants].sort(
    (a, b) => a.name.localeCompare(b.name)
  );

  const lineDiscountTotal = productVariants.reduce(
    (sum, variant) => sum + (variant.discount || 0),
    0
  );
  const totalDiscount = salesInvoice.totalDiscount ?? lineDiscountTotal;

  const deliveryOrderRefs = Array.from(
    new Set(
      productVariants.flatMap(
        (variant) =>
          variant.deliveryOrders?.map((order) => order.referenceNumber) ?? []
      )
    )
  );

  const sellerAddressLines = (salesInvoice.seller?.address ?? "")
    .split("\n")
    .filter(Boolean);
  const buyerAddressLines = (salesInvoice.buyer?.address ?? "")
    .split("\n")
    .filter(Boolean);

  return (
    <Document>
      <Page size={PAGE_SIZE} style={styles.page}>
        <View style={styles.headerRow}>
          <Image style={styles.logo} src="/logo.png" />
          <View style={styles.titleColumn}>
            <Text style={styles.title}>FAKTUR PENJUALAN</Text>
            <Text style={styles.titleRef}>{salesInvoice.referenceNumber}</Text>
          </View>
        </View>

        <View style={styles.infoCard}>
          <View style={styles.infoBandRow}>
            <View style={styles.infoBandCellDivided}>
              <Text style={styles.partyLabel}>PENJUAL :</Text>
              <Text style={styles.partyName}>{salesInvoice.seller?.name}</Text>
              {sellerAddressLines.map((line, idx) => (
                <Text key={idx}>{line}</Text>
              ))}
              {salesInvoice.seller?.taxInformation?.taxIdentificationNumber && (
                <Text style={styles.partyNpwp}>
                  NPWP:{" "}
                  {salesInvoice.seller.taxInformation.taxIdentificationNumber}
                </Text>
              )}
            </View>
            <View style={styles.infoBandCell}>
              <Text style={styles.partyLabel}>PEMBELI :</Text>
              <Text style={styles.partyName}>{salesInvoice.buyer?.name}</Text>
              {buyerAddressLines.map((line, idx) => (
                <Text key={idx}>{line}</Text>
              ))}
              {salesInvoice.buyer?.taxInformation?.taxIdentificationNumber && (
                <Text style={styles.partyNpwp}>
                  NPWP:{" "}
                  {salesInvoice.buyer.taxInformation.taxIdentificationNumber}
                </Text>
              )}
            </View>
          </View>
          <View style={styles.infoBandRowDivided}>
            <View style={styles.infoBandCellDivided}>
              <Text style={styles.metaLabel}>Tanggal Faktur</Text>
              <Text>{formatDate(dayjs(salesInvoice.transactionDate))}</Text>
            </View>
            <View style={styles.infoBandCell}>
              <Text style={styles.metaLabel}>Syarat Pembayaran</Text>
              <Text>{salesInvoice.paymentTerm?.name ?? "-"}</Text>
            </View>
          </View>
          <View style={styles.infoBandRowDivided}>
            <View style={[styles.infoBandCellDivided, { flex: 2 }]}>
              <Text style={styles.metaLabel}>No Ref Surat Jalan</Text>
              <Text>
                {deliveryOrderRefs.length > 0
                  ? deliveryOrderRefs.join(", ")
                  : "-"}
              </Text>
            </View>
            <View style={[styles.infoBandCell, { flex: 1 }]}>
              <Text style={styles.metaLabel}>Catatan</Text>
              <Text>{salesInvoice.note ?? "-"}</Text>
            </View>
          </View>
        </View>

        <View style={styles.tableContainer}>
          <View style={styles.tableHeader}>
            <Text style={[styles.cell, { flex: COL.index }]}>#</Text>
            <Text style={[styles.cell, { flex: COL.name }]}>Nama Barang</Text>
            <Text style={[styles.rightCell, { flex: COL.qty }]}>Qty</Text>
            <Text style={[styles.rightCell, { flex: COL.price }]}>Harga</Text>
            <Text style={[styles.rightCell, { flex: COL.bruto }]}>Bruto</Text>
            <Text style={[styles.rightCell, { flex: COL.disc }]}>Disc</Text>
            <Text
              style={[
                styles.rightCell,
                { flex: COL.total, borderRightWidth: 0 },
              ]}
            >
              Total
            </Text>
          </View>
          {productVariants.map((variant, index) => {
            const bruto = variant.quantity * variant.price;
            const total = bruto - variant.discount;
            const unit = variant.product?.unitOfMeasurement?.name;
            return (
              <View style={styles.tableRow} key={variant.id} wrap={false}>
                <Text style={[styles.cell, { flex: COL.index }]}>
                  {index + 1}
                </Text>
                <Text style={[styles.cell, { flex: COL.name }]}>
                  {variant.name}
                </Text>
                <Text style={[styles.rightCell, { flex: COL.qty }]}>
                  {variant.quantity}
                  {unit ? ` ${unit}` : ""}
                </Text>
                <Text style={[styles.rightCell, { flex: COL.price }]}>
                  {formatNumber(variant.price)}
                </Text>
                <Text style={[styles.rightCell, { flex: COL.bruto }]}>
                  {formatNumber(bruto)}
                </Text>
                <Text style={[styles.rightCell, { flex: COL.disc }]}>
                  {formatNumber(variant.discount)}
                </Text>
                <Text
                  style={[
                    styles.rightCell,
                    { flex: COL.total, borderRightWidth: 0 },
                  ]}
                >
                  {formatNumber(total)}
                </Text>
              </View>
            );
          })}
        </View>

        <View wrap={false}>
          <View style={styles.summarySection}>
            <View style={styles.summaryColumn}>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>SUBTOTAL</Text>
                <Text>{formatMoney(salesInvoice.subtotal)}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>TOTAL DISKON</Text>
                <Text>{formatMoney(totalDiscount)}</Text>
              </View>
              {salesInvoice.taxable && (
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>
                    PPN ({salesInvoice.taxPercentage}%)
                  </Text>
                  <Text>{formatMoney(salesInvoice.taxAmount)}</Text>
                </View>
              )}
              <View style={[styles.summaryRow, styles.grandTotalRow]}>
                <Text>GRAND TOTAL</Text>
                <Text>{formatMoney(salesInvoice.grandTotal)}</Text>
              </View>
            </View>
          </View>

          <View style={styles.signatureSection}>
            {["Admin Penjualan", "Checker"].map((label) => (
              <View key={label} style={styles.signatureCell}>
                <Text style={styles.signatureLabel}>{label}</Text>
                <View style={styles.signatureSubColumn}>
                  <View style={styles.signatureLine} />
                  <Text style={styles.signatureDate}>Tgl.</Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};

SalesInvoicePdf.propTypes = {
  salesInvoice: PropTypes.object.isRequired,
};
